import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Eye, EyeOff, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const createCategorySchema = z.object({
  value: z.string().min(1, "Category name is required").max(50, "Category name too long"),
});

type CreateCategoryForm = z.infer<typeof createCategorySchema>;

const CATEGORY_SETTINGS_QUERY_KEY = ["/api/app-settings", "category"] as const;

interface AppSetting {
  id: number;
  type: string;
  value: string;
  isActive: boolean;
  isDefault?: boolean;
  displayOrder?: number;
  tenantId: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export default function CategoryManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const form = useForm<CreateCategoryForm>({
    resolver: zodResolver(createCategorySchema),
    defaultValues: {
      value: "",
    },
  });

  const refreshCategorySettings = () => {
    queryClient.invalidateQueries({ queryKey: CATEGORY_SETTINGS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ["/api/app-settings"] });
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === "/api/app-settings",
    });
  };

  // Fetch categories
  const { data: categories = [], isLoading } = useQuery<AppSetting[]>({
    queryKey: CATEGORY_SETTINGS_QUERY_KEY,
    queryFn: async () => {
      return await apiRequest("/api/app-settings?type=category");
    },
  });

  // Create new category mutation
  const createCategoryMutation = useMutation({
    mutationFn: async (data: CreateCategoryForm) => {
      return await apiRequest("/api/app-settings", {
        method: "POST",
        body: {
          type: "category",
          value: data.value,
        },
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Category created successfully",
      });
      form.reset();
      refreshCategorySettings();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create category",
        variant: "destructive",
      });
    },
  });

  // Toggle category visibility mutation
  const toggleVisibilityMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      return await apiRequest(`/api/app-settings/${id}`, {
        method: "PATCH",
        body: { isActive },
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Category visibility updated",
      });
      refreshCategorySettings();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update category",
        variant: "destructive",
      });
    },
  });

  // Delete category mutation (soft delete by setting isActive to false)
  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest(`/api/app-settings/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Category archived successfully",
      });
      refreshCategorySettings();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to archive category",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: CreateCategoryForm) => {
    createCategoryMutation.mutate(data);
  };

  const handleToggleVisibility = (category: AppSetting) => {
    toggleVisibilityMutation.mutate({
      id: category.id,
      isActive: !category.isActive,
    });
  };

  const handleDeleteCategory = (id: number) => {
    deleteCategoryMutation.mutate(id);
  };

  const sortedCategories = [...categories].sort((a, b) => {
    // Default categories always first
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    if ((a.displayOrder ?? 0) !== (b.displayOrder ?? 0)) {
      return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
    }
    return a.value.localeCompare(b.value);
  });
  const customCategories = categories.filter(cat => !cat.isDefault);
  const activeCategoriesCount = customCategories.filter(cat => cat.isActive).length;
  const hiddenCategoriesCount = customCategories.filter(cat => !cat.isActive).length;

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">Category Management</h1>
        <p className="text-muted-foreground">
          Manage application categories. Hidden categories won't appear in dropdowns but will preserve historical data.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Default Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">5</div>
            <p className="text-xs text-muted-foreground">
              Available to all organizations
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Custom (Active)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeCategoriesCount}</div>
            <p className="text-xs text-muted-foreground">
              Your organization's visible categories
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Custom (Hidden)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{hiddenCategoriesCount}</div>
            <p className="text-xs text-muted-foreground">
              Hidden but preserved
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Create New Category */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add New Category
          </CardTitle>
          <CardDescription>
            Create a new application category that will be available in dropdown menus.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex gap-4">
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        placeholder="Enter category name (e.g., CRM, Analytics, Security)"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button 
                type="submit" 
                disabled={createCategoryMutation.isPending}
                className="shrink-0"
              >
                {createCategoryMutation.isPending ? "Adding..." : "Add Category"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Categories List */}
      <Card>
        <CardHeader>
          <CardTitle>Existing Categories</CardTitle>
          <CardDescription>
            Manage visibility and organize your application categories. Hidden categories preserve historical data.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading categories...
            </div>
          ) : sortedCategories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No categories found. Create your first custom category above.
            </div>
          ) : (
            <div className="space-y-3">
              {sortedCategories.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Badge variant={category.isActive ? "default" : "secondary"}>
                        {category.value}
                      </Badge>
                      {category.isActive ? (
                        <Eye className="h-4 w-4 text-green-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {category.isActive ? "Visible in dropdowns" : "Hidden from dropdowns"}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`visibility-${category.id}`} className="text-sm">
                        {category.isActive ? "Hide" : "Show"}
                      </Label>
                      <Switch
                        id={`visibility-${category.id}`}
                        checked={category.isActive}
                        onCheckedChange={() => handleToggleVisibility(category)}
                        disabled={toggleVisibilityMutation.isPending}
                      />
                    </div>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Category</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove the category "{category.value}" from your list. It will no longer appear in dropdowns.
                            <br /><br />
                            <strong>This action cannot be undone.</strong>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteCategory(category.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete Category
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
